import { EntityId } from '@reduxjs/toolkit';
import { ROUTER_API } from 'app/constants/api.constant';
import { CouponData, CouponFormType, CouponInDTO, CouponOutDTO } from 'app/main/design-system/models/coupon.type';
import BaseApi from 'app/services/api/base.api';
import axios from 'axios';
import {
  AutomatedMarketingCampaignInDTO,
  CrmFieldFilter,
  Link,
  NotificationType,
} from '../../communicationCampaign/campaign.type';
import { MarketingDTO, MarketingType } from '../types';

export const marketingApi = new BaseApi({ baseURL: ROUTER_API, tokenKey: 'token' });

export const getAutomatedMarketingsApi = async (franchiseId: string) => {
  const response = await marketingApi.api.get<MarketingDTO[]>(`/api/v2/franchises/${franchiseId}/automated-marketings`);
  return response.data || [];
};

export const getTotalMarketingRevenueApi = async (franchiseId: string, automatedMarketingId: MarketingDTO['id']) => {
  const response = await marketingApi.api.get(
    `/api/v2/franchises/${franchiseId}/automated-marketings/${automatedMarketingId}/total-revenue`,
  );
  return response.data;
};

export const createCouponApi = async (
  userId: string,
  payload: Pick<CouponInDTO, 'title' | 'data' | 'validityPeriod' | 'communicationCampaignId'>,
) => {
  const response = await axios.post<CouponInDTO>(`api/users/${userId}/coupons`, payload);
  return response.data;
};

export const getUserCouponApi = async (communicationCampaignId: string) => {
  const response = await marketingApi.api.get<CouponInDTO>(
    `/api/v2/communication-campaigns/${communicationCampaignId}/current-coupon`,
  );
  return response.data;
};

// Answers 404 when the store's new customers receive no welcome coupon.
export const getNewCustomerCouponApi = async (storeId: string) => {
  const response = await marketingApi.api.get<CouponInDTO>('/api/v2/automated-marketings/new-users/coupon', {
    params: { storeId },
  });
  return response.data;
};

export const getAutomatedMarketingDetailApi = async (automatedMarketingId: MarketingDTO['id']) => {
  const response = await marketingApi.api.get<{
    coupon: CouponOutDTO;
    automatedMarketing: MarketingDTO;
    communicationCampaign: AutomatedMarketingCampaignInDTO;
  }>(`/api/v2/automated-marketings/${automatedMarketingId}/detail`);
  return response.data;
};

export const createAutomatedMarketingApi = async (
  userId: string,
  payload: {
    notification?: {
      title: string;
      message: string;
      link?: Link;
    };
    marketingType: MarketingType;
    isActive: boolean;
    communicationCampaignAverageBasket: number;
    communicationCampaignType: NotificationType;
    name: string;
    coupon?: CouponOutDTO;
    campaignFilters?: {
      storeIds: string[];
      crmFieldFilters?: CrmFieldFilter[];
    };
    points?: number;
    sendBefore?: string;
  },
) => {
  const response = await marketingApi.api.post<{ automatedMarketingId: string }>(
    `api/v2/users/${userId}/automated-marketings`,
    payload,
  );
  return response.data;
};

export const updateAutomatedMarketingApi = async (
  userId: string,
  automatedMarketingId: string,
  payload: {
    notification?: {
      title: string;
      message: string;
      link?: Link;
    };
    marketingType: MarketingType;
    isActive: boolean;
    communicationCampaignAverageBasket: number;
    communicationCampaignType: NotificationType;
    name: string;
    coupon?: CouponFormType;
    campaignFilters?: {
      storeIds: string[];
      crmFieldFilters?: CrmFieldFilter[];
    };
    points?: number;
    sendBefore?: string;
  },
) => {
  const response = await marketingApi.api.put<MarketingDTO>(
    `api/v2/users/${userId}/automated-marketings/${automatedMarketingId}`,
    payload,
  );
  return response.data;
};

export const updateAutomatedMarketingCouponApi = async (
  userId: string,
  couponId: string,
  payload: {
    data: CouponData;
    validityPeriod: string;
  },
) => {
  const response = await axios.put<CouponInDTO>(`api/users/${userId}/coupons/${couponId}`, payload);
  return response.data;
};

export const updateCommunicationCampaignApi = async (
  userId: string,
  communicationCampaignId: EntityId,
  payload: {
    notification?: {
      title: string;
      message: string;
    };
  },
) => {
  const response = await axios.put<MarketingDTO>(
    `/api/users/${userId}/communicationCampaign/${communicationCampaignId}`,
    payload,
  );
  return response.data;
};
